import React from 'react'
import { Col, Container, Modal, Row } from 'react-bootstrap'
import ATTextButton from '../common/formComponent/ATTextButton'
import ATTextWithLabel from '../common/formComponent/ATTextWithLabel'
import CustomerDetailsForm from './CustomerDetailsForm'
import ATCheckBox from '../common/formComponent/ATCheckbox'
import CustomerAlert from './CustomerAlert'
import { useTranslation } from 'react-i18next'
import { CustomerDetailData } from '../../interface/response/CustomerResponse'
import useCustomerDetails from '../../customHooks/customer/useCustomerDetails'
import ATLoaderContainer from '../common/loaderContainer/ATLoaderContainer'
import ATModuleMessage from '../common/message/ATModuleMessage'
import { CUSTOMER_APPROVAL_STATUS, CUSTOMER_STATUS } from './customerConstants'
import ATConfirmBox from '../common/confirmBox/ATConfirmBox'
import ATCommentBox from '../common/rejectBox/ATCommentBox'
import Authorize from '../common/auth/Authorize'
import { PERMISSIONS, WIDGET_TYPE } from '../common/auth/AuthorizeConstant'
import { getWidgetMapping, isAuthorized } from '../../utils/authUtil/AuthUtil'
interface CustomerDetailsProps {
  customerInfo: CustomerDetailData | undefined
  isError: boolean
  selectedTab: string
  selectedCustomer: string | undefined
  checkBoxValue: boolean
  setSelectedCustomer: (val: string | undefined) => void
  handleCheckBox: () => void
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customerInfo,
  isError,
  selectedTab,
  selectedCustomer,
  checkBoxValue,
  handleCheckBox,
  setSelectedCustomer,
}) => {
  const { t } = useTranslation(['translation'])
  const {
    showApproveCustomerAlert,
    showRejectCustomerAlert,
    isCustomerDetailsEdit,
    showCustomerDetailsForm,
    handleOnClickApprove,
    handleOnClickReject,
    onCloseCustomerApproveAlert,
    onCloseCustomerRejectAlert,
    onCloseCustomerDetailsFormModal,
    handleCustomerDetailsEdit,
    approveCustomerStatus,
  } = useCustomerDetails()

  const getJSXConditionally = () => {
    if (selectedCustomer === undefined || isError) {
      return (
        <ATLoaderContainer>
          <ATModuleMessage />
        </ATLoaderContainer>
      )
    }
    return (
      <Container fluid className="customer-details-container">
        <Row className="px-2">
          <Col xs={12} className="mt-3">
            <Row className="align-items-center mx-2 px-0 pb-1 border-bottom">
              <Col className="px-0">
                <p className="customer-headers my-0">
                  {t('customer.customer_details')}
                </p>
              </Col>
              {selectedTab !== CUSTOMER_STATUS.REJECTED.eventKey && (
                <Col className="text-end px-0">
                  <Authorize
                    widgetId={getWidgetMapping(
                      PERMISSIONS.EDIT_CUSTOMER_DETAILS,
                    )}
                    widgetType={WIDGET_TYPE.BUTTON}
                  >
                    <ATTextButton
                      isFilledButton={false}
                      title={t('common.edit')}
                      onPressHandler={handleCustomerDetailsEdit}
                    />
                  </Authorize>
                </Col>
              )}
            </Row>
            <Row className="py-2 justify-content-around position-relative">
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.customer_name'}
                  value={customerInfo?.customerName ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.contacts'}
                  value={customerInfo?.contactNumber ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.sales_rep'}
                  value={customerInfo?.salesRep ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.country'}
                  value={customerInfo?.country ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.state'}
                  value={customerInfo?.state ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.city'}
                  value={customerInfo?.city ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.zip_code'}
                  value={customerInfo?.zipCode ?? '--'}
                />
              </Col>
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.billing_address'}
                  value={customerInfo?.address ?? '--'}
                />
              </Col>
            </Row>
            <Row className="py-2 justify-content-around position-relative">
              <Col className="py-3 border-bottom" xs={5}>
                <ATTextWithLabel
                  label={'customer.account_manager'}
                  value={customerInfo?.accountManager ?? '--'}
                />
              </Col>
              <Col className="py-3" xs={5}></Col>
            </Row>
          </Col>
          {selectedTab === CUSTOMER_STATUS.REJECTED.eventKey && (
            <ATCommentBox message={customerInfo?.reasonDescription ?? ''} />
          )}
          {selectedTab === CUSTOMER_STATUS.PENDING.eventKey &&
            isAuthorized(PERMISSIONS.REVIEW_CUSTOMER) && (
              <>
                <Col xs={12} className="px-4 mt-3">
                  <ATCheckBox
                    label={t('common.verifiedMessage')}
                    handleChange={handleCheckBox}
                    selectedValue={false}
                    checked={checkBoxValue}
                    className="customer-confirmation-check"
                  />
                </Col>
                <div className="d-flex py-3 justify-content-end">
                  <div>
                    <Authorize
                      widgetId={getWidgetMapping(PERMISSIONS.REVIEW_CUSTOMER)}
                      widgetType={WIDGET_TYPE.DROPDOWN_ITEM}
                    >
                      <ATTextButton
                        title={t('common.reject')}
                        onPressHandler={handleOnClickReject}
                        isFilledButton={false}
                        customClassName="text-uppercase"
                      />
                    </Authorize>
                  </div>
                  <div>
                    <Authorize
                      widgetId={getWidgetMapping(PERMISSIONS.REVIEW_CUSTOMER)}
                      widgetType={WIDGET_TYPE.DROPDOWN_ITEM}
                    >
                      <ATTextButton
                        title={t('common.approve')}
                        onPressHandler={handleOnClickApprove}
                        disabled={!checkBoxValue}
                        customClassName="text-uppercase"
                      />
                    </Authorize>
                  </div>
                </div>
              </>
            )}
        </Row>
      </Container>
    )
  }
  return (
    <div className="container-fluid h-100 scroll-list-content">
      {getJSXConditionally()}
      <Modal show={showCustomerDetailsForm} className="modal-lg">
        <CustomerDetailsForm
          isEdit={isCustomerDetailsEdit}
          onCloseModal={onCloseCustomerDetailsFormModal}
          data={customerInfo}
        />
      </Modal>
      <Modal show={showRejectCustomerAlert} className="modal-md">
        <CustomerAlert
          onCloseModal={onCloseCustomerRejectAlert}
          code={customerInfo?.customerCode ?? ''}
          setSelectedCustomer={setSelectedCustomer}
        />
      </Modal>
      <ATConfirmBox
        showModal={showApproveCustomerAlert}
        modalMessage={t('modal.approval_message', { property: 'customer' })}
        modalTitle={t('common.alert')}
        onSave={() => {
          approveCustomerStatus({
            customerCode: customerInfo?.customerCode,
            status: CUSTOMER_APPROVAL_STATUS.APPROVED,
            reasonDescription: '',
          })
          setSelectedCustomer(undefined)
        }}
        onCancel={onCloseCustomerApproveAlert}
      />
    </div>
  )
}
export default CustomerDetails
